import { fromJS } from 'immutable';
import dayjs from 'dayjs';
import omit from 'lodash/omit';
import type { Business } from '../dashboard/dashboardTypes';
import type { Email, EmailAction, EmailTemplate } from './emailTypes';
import { EmailActionTypes as actionTypes } from './emailTypes';

const initialState = fromJS({
	contacted: {
		emailContacted: false,
		lastContactedDate: null,
		loading: false
	},
	email: {
		isLoading: false,
		queue: {},
		success: false,
		failure: '',
		failureByBusiness: {},
		subject: 'Space available',
		body: "I'm working with a beautiful shopping center in town. We have a space available. Do you have any plans to relocate or expand? I was reading about [business_name] and thought you might be a good fit. Let me know and I can send you a flyer or set up a tour.",
		sent: {},
		monthlyLimit: '',
		statuses: {
			data: {},
			loading: false,
			failure: ''
		},
		scheduled: {
			activity: {},
			limits: {},
			emails: [],
			countByDate: null,
			countByTimePeriod: {
				data: [],
				loading: false
			},
			cancel: {
				loading: false,
				success: false,
				failure: false
			},
			failure: ''
		},
		sendAll: {
			loading: false,
			success: false,
			failure: ''
		}
	},
	templates: {
		data: [],
		failure: '',
		createSucceed: false,
		deleteSucceed: false,
		isLoading: false,
		autoSaveLoading: false,
		autoSaveSuccess: false,
		stale: {},
		lastStaledTemplate: {},
		unstaledTemplate: {},
		scores: {
			data: {},
			loading: false,
			failure: false
		}
	},
	ai: {
		text: {},
		templates: {},
		revertTemplate: false,
		loading: false,
		failure: false,
		success: false,
		credits: 0,
		regenerate: {
			data: {},
			loading: {},
			successId: '',
			failure: false
		}
	},
	reportBadData: {
		loading: false,
		success: false,
		failure: false
	}
});

export default (state = initialState, action: EmailAction, rootState: any) => {
	switch (action.type) {
		case actionTypes.EMAIL_UPDATE_EMAIL: {
			const { subject, body, businessId, email, name } = action.payload;

			let newState;

			if (businessId) {
				newState = state.setIn(
					['email', 'edited', businessId],
					fromJS({
						subject,
						body,
						businessId,
						email,
						name
					})
				);
			} else {
				newState = state
					.setIn(['email', 'subject'], subject)
					.setIn(['email', 'body'], body);
			}

			return newState;
		}
		case actionTypes.EMAIL_SEND_EMAILS_REQUEST: {
			return state
				.setIn(['email', 'isLoading'], true)
				.setIn(['email', 'success'], false)
				.setIn(['email', 'failure'], '')
				.setIn(['email', 'scheduled', 'failure'], '');
		}

		case actionTypes.EMAIL_SEND_EMAILS_SUCCESS: {
			const emails: Email[] = action.payload;
			const businesses = rootState.dashboard
				.getIn(['businesses', 'data'])
				.toJS();
			const commBusinesses = rootState.dashboard.getIn([
				'businesses',
				'communicationCenter',
				'data'
			]);
			let newState = state;

			const emailFailure = state
				.getIn(['email', 'failureByBusiness'])
				.toJS();

			if (businesses.length === 0 && commBusinesses.length === 0) {
				const business = rootState.dashboard.getIn([
					'businesses',
					'modalBusiness'
				]);

				const email = emails.find(
					({ businessId }: Email) => businessId === business.id
				);

				if (email) {
					if (emailFailure[business.id]) {
						const newEmailFailure = {
							...emailFailure,
							[business.id]: null
						};
						newState = newState.setIn(
							['email', 'failureByBusiness'],
							fromJS(newEmailFailure)
						);
					}

					newState = newState
						.setIn(
							['email', 'sent', business.id],
							fromJS({
								subject: email.subject,
								body: email.body,
								businessId: business.id,
								email: email.email,
								name: business.name
							})
						)
						.removeIn(['email', 'edited', business.id]);
				}
			}

			businesses.forEach((business: Business) => {
				const newBusiness: Business = fromJS(business).toJS();
				const email = emails.find(
					({ businessId }: Email) => businessId === newBusiness._id
				);

				if (email) {
					if (emailFailure[business._id]) {
						const newEmailFailure = {
							...emailFailure,
							[business._id]: null
						};

						newState = newState.setIn(
							['email', 'failureByBusiness'],
							fromJS(newEmailFailure)
						);
					}

					newState = newState
						.setIn(
							['email', 'sent', business._id],
							fromJS({
								subject: email.subject,
								body: email.body,
								businessId: newBusiness._id,
								email: email.email,
								name: newBusiness.name
							})
						)
						.removeIn(['email', 'edited', newBusiness._id]);
				}
			});

			if (commBusinesses && commBusinesses.length > 0) {
				commBusinesses.forEach((business: Business) => {
					const newBusiness: Business = fromJS(business).toJS();
					const email = emails.find(({ businessId }: Email) => {
						return businessId === newBusiness.businessId;
					});

					if (email) {
						newState = newState
							.setIn(
								['email', 'sent', newBusiness.businessId],
								fromJS({
									subject: email.subject,
									body: email.body,
									businessId: newBusiness.businessId,
									email: email.email,
									name: newBusiness.name
								})
							)
							.removeIn([
								'email',
								'edited',
								newBusiness.businessId
							]);
					}
				});
			}

			if (action.payload[0].isScheduledEmail) {
				return newState
					.setIn(['email', 'isLoading'], false)
					.setIn(['email', 'success'], true)
					.setIn(['email', 'failure'], '')
					.setIn(['email', 'replyMessageId'], '');
			}

			const queue = state.getIn(['email', 'queue']).toJS();
			const newQueue = { ...queue, [action.payload[0].businessId]: true };

			return newState
				.setIn(['email', 'isLoading'], false)
				.setIn(['email', 'queue'], fromJS(newQueue))
				.setIn(['email', 'success'], true)
				.setIn(['email', 'failure'], '')
				.setIn(['email', 'replyMessageId'], '');
		}
		case actionTypes.EMAIL_SEND_EMAILS_FAILURE: {
			const { message, businessId }: any = action.error!;
			const failure = state.getIn(['email', 'failureByBusiness']).toJS();
			const newFailure = { ...failure, [businessId]: message };

			return state
				.setIn(['email', 'isLoading'], false)
				.setIn(['email', 'success'], false)
				.setIn(['email', 'failure'], message)
				.setIn(['email', 'failureByBusiness'], fromJS(newFailure));
		}
		case actionTypes.EMAIL_CLEAR_EMAILS_STATUS: {
			return state
				.setIn(['email', 'success'], false)
				.setIn(['email', 'failure'], '');
		}
		case actionTypes.EMAIL_RESET_EMAIL: {
			return state
				.setIn(['email', 'isLoading'], false)
				.setIn(['email', 'success'], false)
				.setIn(['email', 'edited'], fromJS({}))
				.setIn(['email', 'failure'], '')
				.setIn(['email', 'failureByBusiness'], fromJS({}))
				.setIn(
					['email', 'body'],
					"I'm working with a beautiful shopping center in town. We have a space available. Do you have any plans to relocate or expand? I was reading about [business_name] and thought you might be a good fit. Let me know and I can send you a flyer or set up a tour."
				)
				.setIn(['email', 'sent'], fromJS({}));
		}
		case actionTypes.EMAIL_ADD_REPLY_SUBJECT_FOR_EMAIL: {
			const { subject } = action.payload;
			return state.setIn(['email', 'replySubject'], subject);
		}
		case actionTypes.EMAIL_REMOVE_REPLY_SUBJECT_FOR_EMAIL: {
			return state.setIn(['email', 'replySubject'], '');
		}
		case actionTypes.EMAIL_REPLY_MESSAGE_ID: {
			const { messageId } = action.payload;
			return state.setIn(['email', 'replyMessageId'], messageId);
		}
		case actionTypes.EMAIL_GET_EMAIL_TEMPLATES_REQUEST:
			return state.setIn(['templates', 'isLoading'], true);
		case actionTypes.EMAIL_GET_EMAIL_TEMPLATES_SUCCESS: {
			const { data } = action.payload;

			return state
				.setIn(['templates', 'data'], fromJS(data))
				.setIn(['templates', 'isLoading'], false);
		}

		case actionTypes.EMAIL_CREATE_EMAIL_TEMPLATE_REQUEST: {
			return state
				.setIn(['templates', 'autoSaveLoading'], false)
				.setIn(['templates', 'createSucceed'], false);
		}
		case actionTypes.EMAIL_CREATE_EMAIL_TEMPLATE_SUCCESS: {
			const { data } = action.payload;
			const emailTemplates: EmailTemplate[] = state.getIn([
				'templates',
				'data'
			]);
			return state
				.setIn(
					['templates', 'data'],
					emailTemplates.unshift(fromJS(data))
				)
				.setIn(['templates', 'autoSaveLoading'], false)
				.setIn(['templates', 'createSucceed'], true);
		}
		case actionTypes.EMAIL_UPDATE_EMAIL_TEMPLATE_REQUEST: {
			if (action.payload.isStale) {
				return state.setIn(['templates', 'autoSaveSuccess'], false);
			}

			return state
				.setIn(['templates', 'autoSaveLoading'], true)
				.setIn(['templates', 'autoSaveSuccess'], false);
		}
		case actionTypes.EMAIL_UPDATE_EMAIL_TEMPLATE_SUCCESS: {
			const { template, templateEditDate } = action.payload;
			const emailTemplates: EmailTemplate[] = state
				.getIn(['templates', 'data'])
				.toJS();

			let foundIndex = 0;

			const found = emailTemplates.find((t, index) => {
				if (t.id === template.id) {
					foundIndex = index;

					return true;
				}

				return false;
			});

			const stale = state.getIn(['templates', 'stale']).toJS();

			const newTemplate = {
				...found,
				...template,
				templateEditDate,
				isStale: false,
				templateUsage: 0
			};

			if (template.isStale && stale[template.id]) {
				const newStale = omit(stale, template.id);

				return state
					.setIn(['templates', 'autoSaveLoading'], false)
					.setIn(['templates', 'autoSaveSuccess'], true)
					.setIn(['templates', 'stale'], fromJS(newStale))
					.setIn(
						['templates', 'unstaledTemplate'],
						fromJS(newTemplate)
					)
					.setIn(
						['templates', 'data', foundIndex],
						fromJS(newTemplate)
					);
			}

			if (template.isStale) {
				return state
					.setIn(['templates', 'autoSaveLoading'], false)
					.setIn(['templates', 'autoSaveSuccess'], true)
					.setIn(
						['templates', 'unstaledTemplate'],
						fromJS(newTemplate)
					)
					.setIn(
						['templates', 'data', foundIndex],
						fromJS(newTemplate)
					);
			}

			return state
				.setIn(['templates', 'autoSaveLoading'], false)
				.setIn(['templates', 'autoSaveSuccess'], true)
				.setIn(
					['templates', 'data', foundIndex],
					fromJS({ ...found, ...template })
				);
		}

		case actionTypes.EMAIL_UPDATE_EMAIL_TEMPLATE_FAILURE: {
			return state.setIn(['templates', 'autoSaveLoading'], false);
		}
		case actionTypes.EMAIL_DELETE_EMAIL_TEMPLATE_REQUEST: {
			const { id } = action.payload;
			const emailTemplates: EmailTemplate[] = state
				.getIn(['templates', 'data'])
				.toJS();

			return state
				.setIn(['templates', 'deleteSucceed'], false)
				.removeIn([
					'templates',
					'data',
					emailTemplates.findIndex(t => t.id === id)
				]);
		}
		case actionTypes.EMAIL_DELETE_EMAIL_TEMPLATE_SUCCESS: {
			return state.setIn(['templates', 'deleteSucceed'], true);
		}
		case actionTypes.EMAIL_GET_EMAIL_CONTACTED_STATUS_LOADING: {
			return state.setIn(['contacted', 'loading'], true);
		}
		case actionTypes.EMAIL_GET_EMAIL_CONTACTED_STATUS_SUCCESS: {
			const { emailContacted, lastContactedDate, contactedDetails } =
				action.payload;
			return state
				.setIn(['contacted', 'contactedDetails'], contactedDetails)
				.setIn(['contacted', 'emailContacted'], emailContacted)
				.setIn(['contacted', 'lastContactedDate'], lastContactedDate)
				.setIn(['contacted', 'loading'], false);
		}
		case actionTypes.EMAIL_GET_EMAIL_CONTACTED_STATUS_FAILURE: {
			return state.setIn(['contacted', 'loading'], false);
		}
		case actionTypes.EMAIL_CLEAR_EMAIL_CONTACTED_STATUS: {
			return state.setIn(['contacted', 'emailContacted'], false);
		}
		case actionTypes.EMAIL_GET_EMAIL_STATUSES_LOADING: {
			return state
				.setIn(['email', 'statuses', 'loading'], true)
				.setIn(['email', 'statuses', 'data'], fromJS({}))
				.setIn(['email', 'statuses', 'failure'], '');
		}
		case actionTypes.EMAIL_GET_EMAIL_STATUSES_SUCCESS: {
			return state
				.setIn(['email', 'statuses', 'loading'], false)
				.setIn(
					['email', 'statuses', 'data'],
					action.payload ? fromJS(action.payload) : fromJS({})
				)
				.setIn(['email', 'statuses', 'failure'], '');
		}
		case actionTypes.EMAIL_GET_EMAIL_STATUSES_FAILURE: {
			const { message } = action.error!;
			return state
				.setIn(['email', 'statuses', 'loading'], false)
				.setIn(['email', 'statuses', 'data'], fromJS({}))
				.setIn(['email', 'statuses', 'failure'], message);
		}
		case actionTypes.EMAIL_CLEAR_EMAIL_STATUSES: {
			return state
				.setIn(['email', 'statuses', 'loading'], false)
				.setIn(['email', 'statuses', 'data'], fromJS({}))
				.setIn(['email', 'statuses', 'failure'], '');
		}
		case actionTypes.EMAIL_CLEAR_EMAIL_QUEUE: {
			return state.setIn(['email', 'queue'], fromJS({}));
		}
		case actionTypes.EMAIL_GET_SCHEDULED_EMAIL_LIMITS_SUCCESS: {
			const { activity, limits } = action.payload;
			return state
				.setIn(['email', 'scheduled', 'activity'], fromJS(activity))
				.setIn(['email', 'scheduled', 'limits'], fromJS(limits));
		}
		case actionTypes.EMAIL_SEND_SCHEDULED_EMAIL_SUCCESS: {
			const activity = state
				.getIn(['email', 'scheduled', 'activity'])
				.toJS();
			const limits = state.getIn(['email', 'scheduled', 'limits']).toJS();
			const emailLimit = rootState.user.getIn([
				'authorize',
				'user',
				'emailLimit'
			]);
			const { businessId, scheduleDate, requestedScheduleDate } =
				action.payload;
			const scheduleDateDashes = dayjs(
				requestedScheduleDate,
				'MM/DD/YYYY, h:mm:ss A'
			).format('MM-DD-YYYY');
			const scheduleDateAndTime = dayjs(
				requestedScheduleDate,
				'MM/DD/YYYY, h:mm:ss A'
			).format('MM-DD-YYYY-HH-mm');

			let newLimits = { ...limits };

			if (newLimits[scheduleDateDashes]?.units[scheduleDateAndTime]) {
				newLimits[scheduleDateDashes].limit -= 1;
				newLimits[scheduleDateDashes].units[
					scheduleDateAndTime
				].limit -= 1;
			} else if (newLimits[scheduleDateDashes]) {
				newLimits = {
					...newLimits,
					[scheduleDateDashes]: {
						...newLimits[scheduleDateDashes],
						units: {
							...newLimits[scheduleDateDashes].units,
							[scheduleDateAndTime]: {
								limit: 10
							}
						}
					}
				};
				newLimits[scheduleDateDashes].limit -= 1;
				newLimits[scheduleDateDashes].units[
					scheduleDateAndTime
				].limit -= 1;
			} else {
				newLimits = {
					...newLimits,
					[scheduleDateDashes]: {
						limit: emailLimit,
						units: { [scheduleDateAndTime]: { limit: 10 } }
					}
				};
				newLimits[scheduleDateDashes].limit -= 1;
				newLimits[scheduleDateDashes].units[
					scheduleDateAndTime
				].limit -= 1;
			}

			const newActivity = {
				...activity,
				[businessId]: { requestedScheduleDate, scheduleDate }
			};

			return state
				.setIn(['email', 'scheduled', 'activity'], fromJS(newActivity))
				.setIn(['email', 'scheduled', 'limits'], fromJS(newLimits))
				.setIn(['email', 'scheduled', 'failure'], '')
				.setIn(['email', 'isLoading'], false);
		}
		case actionTypes.EMAIL_SEND_SCHEDULED_EMAIL_FAILURE: {
			let failureMsg;

			if (action.payload?.data?.message) {
				failureMsg = 'Email is already scheduled for this business';
			} else {
				failureMsg = 'Generic';
			}

			return state
				.setIn(['email', 'scheduled', 'failure'], failureMsg)
				.setIn(['email', 'isLoading'], false);
		}
		case actionTypes.EMAIL_SEND_SCHEDULED_EMAIL_CLEAR_FAILURE_STATUS: {
			return state
				.setIn(['email', 'scheduled', 'failure'], '')
				.setIn(['email', 'isLoading'], false);
		}

		case actionTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN: {
			return state
				.setIn(['email', 'scheduledbatch', 'cancel', 'loading'], true)
				.setIn(['email', 'scheduledbatch', 'cancel', 'success'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'failure'], false);
		}

		case actionTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN_SUCCESS: {
			alert(
				`You've cancelled ${action.payload.successfulCancellations} emails, and ${action.payload.failedCancellations} emails failed to cancel`
			);
			return state
				.setIn(['email', 'scheduledbatch', 'cancel', 'loading'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'success'], true)
				.setIn(['email', 'scheduledbatch', 'cancel', 'failure'], false);
		}

		case actionTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_ADMIN_FAILURE: {
			alert(`Failed to cancel emails`);
			return state
				.setIn(['email', 'scheduledbatch', 'cancel', 'loading'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'success'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'failure'], true);
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER: {
			return state
				.setIn(['email', 'scheduledbatch', 'cancel', 'loading'], true)
				.setIn(['email', 'scheduledbatch', 'cancel', 'success'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'failure'], false);
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER_SUCCESS: {
			const scheduledEmails = state.getIn([
				'email',
				'scheduled',
				'emails'
			]);
			const newScheduledEmails = scheduledEmails.filter(
				email => !action.payload.includes(email.businessId)
			);
			const activity = state
				.getIn(['email', 'scheduled', 'activity'])
				.toJS();
			const limits = state.getIn(['email', 'scheduled', 'limits']).toJS();
			const emailLimit = rootState.user.getIn([
				'authorize',
				'user',
				'emailLimit'
			]);

			const newLimits = { ...limits };
			let newActivity = { ...activity };

			action.payload.forEach((businessId: string) => {
				const scheduleDate = dayjs(
					activity[businessId].requestedScheduleDate,
					'MM/DD/YYYY, h:mm:ss A'
				).format('MM-DD-YYYY');
				const scheduleDateAndTime = dayjs(
					activity[businessId].requestedScheduleDate,
					'MM/DD/YYYY, h:mm:ss A'
				).format('MM-DD-YYYY-HH-mm');

				if (newLimits[scheduleDate]?.limit < emailLimit) {
					newLimits[scheduleDate].limit += 1;
				}

				if (
					newLimits[scheduleDate].units[scheduleDateAndTime]?.limit <
					10
				) {
					newLimits[scheduleDate].units[scheduleDateAndTime].limit +=
						1;
				}

				newActivity = omit(newActivity, businessId);
			});

			return state
				.setIn(['email', 'scheduled', 'activity'], fromJS(newActivity))
				.setIn(['email', 'scheduled', 'limits'], fromJS(newLimits))
				.setIn(['email', 'scheduled', 'emails'], newScheduledEmails)
				.setIn(['email', 'scheduledbatch', 'cancel', 'loading'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'success'], true)
				.setIn(['email', 'scheduledbatch', 'cancel', 'failure'], false);
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_USER_FAILURE: {
			return state
				.setIn(['email', 'scheduledbatch', 'cancel', 'loading'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'success'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'failure'], true);
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_BATCH_EMAILS_CLEAR_STATUS: {
			return state
				.setIn(['email', 'scheduledbatch', 'cancel', 'success'], false)
				.setIn(['email', 'scheduledbatch', 'cancel', 'failure'], false);
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_EMAIL: {
			return state
				.setIn(['email', 'scheduled', 'cancel', 'loading'], true)
				.setIn(['email', 'scheduled', 'cancel', 'success'], false)
				.setIn(['email', 'scheduled', 'cancel', 'failure'], false);
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_EMAIL_SUCCESS: {
			const { businessId, data } = action.payload;
			const { templateId, emailCreatedDate } = data;
			const activity = state
				.getIn(['email', 'scheduled', 'activity'])
				.toJS();
			const limits = state.getIn(['email', 'scheduled', 'limits']).toJS();
			const emailLimit = rootState.user.getIn([
				'authorize',
				'user',
				'emailLimit'
			]);

			const scheduleDate = dayjs(
				activity[businessId].requestedScheduleDate,
				'MM/DD/YYYY, h:mm:ss A'
			).format('MM-DD-YYYY');
			const scheduleDateAndTime = dayjs(
				activity[businessId].requestedScheduleDate,
				'MM/DD/YYYY, h:mm:ss A'
			).format('MM-DD-YYYY-HH-mm');

			const newLimits = { ...limits };

			if (newLimits[scheduleDate]?.limit < emailLimit) {
				newLimits[scheduleDate].limit += 1;
			}

			if (
				newLimits[scheduleDate].units[scheduleDateAndTime]?.limit < 10
			) {
				newLimits[scheduleDate].units[scheduleDateAndTime].limit += 1;
			}

			const newActivity = omit(activity, businessId);

			let newState = state
				.setIn(['email', 'scheduled', 'activity'], fromJS(newActivity))
				.setIn(['email', 'scheduled', 'limits'], fromJS(newLimits))
				.setIn(['email', 'scheduled', 'cancel', 'loading'], false)
				.setIn(['email', 'scheduled', 'cancel', 'success'], true)
				.setIn(['email', 'scheduled', 'cancel', 'failure'], false);

			const templates = state.getIn(['templates', 'data']).toJS();
			const index = templates.findIndex(
				(template: EmailTemplate) => template.id === templateId
			);
			const newTemplates = [...templates];

			const templateEditDate = dayjs(
				newTemplates[index]?.templateEditDate
			);
			const emailInitiatedDate = dayjs(emailCreatedDate);
			const timeDifference = emailInitiatedDate.diff(templateEditDate);

			if (
				!newTemplates[index]?.templateEditDate &&
				!newTemplates[index]?.defaultTemplate &&
				newTemplates[index]?.templateUsage
			) {
				newTemplates[index].templateUsage -= 1;
			} else if (
				timeDifference >= 0 &&
				newTemplates[index]?.templateUsage
			) {
				newTemplates[index].templateUsage -= 1;
			}

			if (newTemplates[index]?.templateUsage === 499) {
				const stale = state.getIn(['templates', 'stale']).toJS();

				if (stale[templateId]) {
					const newStale = omit(stale, templateId);
					newState = newState.setIn(
						['templates', 'stale'],
						fromJS(newStale)
					);
				}

				newTemplates[index].isStale = false;
				newState = newState.setIn(
					['templates', 'unstaledTemplate'],
					fromJS(newTemplates[index])
				);
			}

			return newState.setIn(['templates', 'data'], fromJS(newTemplates));
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_EMAIL_FAILURE: {
			return state
				.setIn(['email', 'scheduled', 'cancel', 'loading'], false)
				.setIn(['email', 'scheduled', 'cancel', 'success'], false)
				.setIn(['email', 'scheduled', 'cancel', 'failure'], true);
		}
		case actionTypes.EMAIL_CANCEL_SCHEDULED_EMAIL_CLEAR_STATUS: {
			return state
				.setIn(['email', 'scheduled', 'cancel', 'loading'], false)
				.setIn(['email', 'scheduled', 'cancel', 'success'], false)
				.setIn(['email', 'scheduled', 'cancel', 'failure'], false);
		}
		case actionTypes.EMAIL_GET_SCHEDULED_EMAILS_SUCCESS: {
			return state.setIn(
				['email', 'scheduled', 'emails'],
				action.payload
			);
		}
		case actionTypes.EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_DATE_SUCCESS: {
			return state.setIn(
				['email', 'scheduled', 'countByDate'],
				action.payload
			);
		}
		case actionTypes.EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_TIME_PERIOD: {
			return state.setIn(
				['email', 'scheduled', 'countByTimePeriod', 'loading'],
				true
			);
		}
		case actionTypes.EMAIL_GET_SCHEDULED_EMAILS_COUNT_BY_TIME_PERIOD_SUCCESS: {
			return state
				.setIn(
					['email', 'scheduled', 'countByTimePeriod', 'loading'],
					false
				)
				.setIn(
					['email', 'scheduled', 'countByTimePeriod', 'data'],
					action.payload
				);
		}
		case actionTypes.EMAIL_GET_EMAIL_TEMPLATE_SCORES: {
			return state
				.setIn(['templates', 'scores', 'failure'], false)
				.setIn(['templates', 'scores', 'loading'], true);
		}
		case actionTypes.EMAIL_CLEAR_EMAIL_TEMPLATE_SCORES: {
			return state
				.setIn(['templates', 'scores', 'failure'], false)
				.setIn(['templates', 'scores', 'loading'], false)
				.setIn(['templates', 'scores', 'data'], fromJS({}));
		}
		case actionTypes.EMAIL_GET_EMAIL_TEMPLATE_SCORES_SUCCESS: {
			const { id, data } = action.payload;
			const scores = state.getIn(['templates', 'scores', 'data']).toJS();

			const newScores = { ...scores, [id]: data };

			return state
				.setIn(['templates', 'scores', 'loading'], false)
				.setIn(['templates', 'scores', 'failure'], false)
				.setIn(['templates', 'scores', 'data'], fromJS(newScores));
		}
		case actionTypes.EMAIL_GET_EMAIL_TEMPLATE_SCORES_FAILURE: {
			return state
				.setIn(['templates', 'scores', 'failure'], true)
				.setIn(['templates', 'scores', 'loading'], false);
		}
		case actionTypes.EMAIL_INCREASE_EMAIL_TEMPLATE_USAGE: {
			const templates = state.getIn(['templates', 'data']).toJS();
			const index = templates.findIndex(
				(template: EmailTemplate) => template.id === action.payload
			);

			if (index < 0) {
				return;
			}

			const newTemplates = [...templates];

			if (
				!newTemplates[index]?.templateUsage &&
				!newTemplates[index]?.defaultTemplate
			) {
				newTemplates[index].templateUsage = 1;

				return state.setIn(['templates', 'data'], fromJS(newTemplates));
			}

			if (!newTemplates[index]?.defaultTemplate) {
				newTemplates[index].templateUsage += 1;
			}

			if (newTemplates[index].templateUsage === 500) {
				const stale = state.getIn(['templates', 'stale']).toJS();
				const newStale = { ...stale, [action.payload]: true };
				newTemplates[index].isStale = true;

				return state
					.setIn(['templates', 'data'], fromJS(newTemplates))
					.setIn(['templates', 'stale'], fromJS(newStale))
					.setIn(
						['templates', 'lastStaledTemplate'],
						fromJS(newTemplates[index])
					);
			}

			return state.setIn(['templates', 'data'], fromJS(newTemplates));
		}
		case actionTypes.EMAIL_CLEAR_STALE_TEMPLATE_STATE: {
			return state.setIn(['templates', 'stale'], fromJS({}));
		}
		case actionTypes.EMAIL_CLEAR_UNSTALED_TEMPLATE: {
			return state.setIn(['templates', 'unstaledTemplate'], fromJS({}));
		}
		case actionTypes.EMAIL_CLEAR_LAST_STALED_TEMPLATE: {
			return state.setIn(['templates', 'lastStaledTemplate'], fromJS({}));
		}
		case actionTypes.EMAIL_GET_AI_SNIPPETS: {
			return state.setIn(['ai', 'loading'], true);
		}
		case actionTypes.EMAIL_GET_AI_SNIPPETS_SUCCESS: {
			const { aiText, creditsAvailable, listId, emailTemplate } =
				action.payload;
			const currentAiText = state.getIn(['ai', 'text']).toJS();
			let newAiText = {};

			if (currentAiText?.[listId]?.[emailTemplate.id]) {
				newAiText = {
					...currentAiText,
					[listId]: {
						...currentAiText[listId],
						[emailTemplate.id]: {
							...currentAiText[listId][emailTemplate.id],
							...aiText
						}
					}
				};
			} else {
				newAiText = {
					...currentAiText,
					[listId]: {
						...currentAiText[listId],
						[emailTemplate.id]: { ...aiText }
					}
				};
			}

			const aiTemplates = state.getIn(['ai', 'templates']).toJS();
			const newAiTemplates = {
				...aiTemplates,
				[emailTemplate.id]: emailTemplate
			};

			return state
				.setIn(['ai', 'text'], fromJS(newAiText))
				.setIn(['ai', 'templates'], fromJS(newAiTemplates))
				.setIn(['ai', 'credits'], creditsAvailable)
				.setIn(['ai', 'loading'], false)
				.setIn(['ai', 'success'], true);
		}
		case actionTypes.EMAIL_CLEAR_AI_SNIPPETS_SUCCESS: {
			return state.setIn(['ai', 'success'], false);
		}
		case actionTypes.EMAIL_DELETE_AI_SNIPPETS: {
			const { listId, templateId } = action.payload;
			const currentAiText = state.getIn(['ai', 'text']).toJS();
			const newAiText = {
				...currentAiText,
				[listId]: {
					...currentAiText[listId],
					[templateId]: {}
				}
			};

			return state.setIn(['ai', 'text'], fromJS(newAiText));
		}
		case actionTypes.EMAIL_REGENERATE_AI_SNIPPET: {
			const { listId, emailTemplate, businessId } = action.payload;
			const regenerateLoading = state
				.getIn(['ai', 'regenerate', 'loading'])
				.toJS();
			const newRegenerateLoading = {
				...regenerateLoading,
				[listId]: {
					...regenerateLoading[listId],
					[emailTemplate.id]: {
						...regenerateLoading?.[listId]?.[emailTemplate.id],
						[businessId]: true
					}
				}
			};

			return state
				.setIn(
					['ai', 'regenerate', 'loading'],
					fromJS(newRegenerateLoading)
				)
				.setIn(['ai', 'regenerate', 'failure'], false);
		}
		case actionTypes.EMAIL_REGENERATE_AI_SNIPPET_SUCCESS: {
			const {
				aiText,
				listId,
				emailTemplateId,
				creditsAvailable,
				businessId
			} = action.payload;
			const currentAiText = state.getIn(['ai', 'text']).toJS();
			const currentAiRegenerated = state
				.getIn(['ai', 'regenerate', 'data'])
				.toJS();
			const regenerateLoading = state
				.getIn(['ai', 'regenerate', 'loading'])
				.toJS();

			let newAiText = {};

			if (currentAiText?.[listId]?.[emailTemplateId]) {
				newAiText = {
					...currentAiText,
					[listId]: {
						...currentAiText[listId],
						[emailTemplateId]: {
							...currentAiText[listId][emailTemplateId],
							...aiText
						}
					}
				};
			} else {
				newAiText = {
					...currentAiText,
					[listId]: {
						...currentAiText[listId],
						[emailTemplateId]: { ...aiText }
					}
				};
			}

			let newAiRegenerated = {};

			if (currentAiRegenerated?.[listId]?.[emailTemplateId]) {
				newAiRegenerated = {
					...currentAiRegenerated,
					[listId]: {
						...currentAiRegenerated[listId],
						[emailTemplateId]: {
							...currentAiRegenerated[listId][emailTemplateId],
							[Object.keys(aiText)[0]]: true
						}
					}
				};
			} else {
				newAiRegenerated = {
					...currentAiRegenerated,
					[listId]: {
						...currentAiRegenerated[listId],
						[emailTemplateId]: { [Object.keys(aiText)[0]]: true }
					}
				};
			}

			const newRegenerateLoading = {
				...regenerateLoading,
				[listId]: {
					...regenerateLoading[listId],
					[emailTemplateId]: {
						...regenerateLoading?.[listId]?.[emailTemplateId],
						[businessId]: false
					}
				}
			};

			return state
				.setIn(['ai', 'text'], fromJS(newAiText))
				.setIn(['ai', 'credits'], creditsAvailable)
				.setIn(['ai', 'loading'], false)
				.setIn(['ai', 'regenerate', 'successId'], businessId)
				.setIn(
					['ai', 'regenerate', 'loading'],
					fromJS(newRegenerateLoading)
				)
				.setIn(['ai', 'regenerate', 'data'], fromJS(newAiRegenerated));
		}
		case actionTypes.EMAIL_REGENERATE_AI_SNIPPET_FAILURE: {
			const { listId, emailTemplateId, businessId } = action.payload;
			const regenerateLoading = state
				.getIn(['ai', 'regenerate', 'loading'])
				.toJS();
			const newRegenerateLoading = {
				...regenerateLoading,
				[listId]: {
					...regenerateLoading[listId],
					[emailTemplateId]: {
						...regenerateLoading?.[listId]?.[emailTemplateId],
						[businessId]: false
					}
				}
			};

			return state
				.setIn(
					['ai', 'regenerate', 'loading'],
					fromJS(newRegenerateLoading)
				)
				.setIn(['ai', 'regenerate', 'failure'], true);
		}
		case actionTypes.EMAIL_CLEAR_AI_REGENERATE: {
			return state.setIn(['ai', 'regenerate', 'successId'], '');
		}
		case actionTypes.EMAIL_GET_AI_CREDITS_SUCCESS: {
			const { credits } = action.payload;

			return state.setIn(['ai', 'credits'], credits);
		}
		case actionTypes.EMAIL_SEND_ALL: {
			return state
				.setIn(['email', 'sendAll', 'loading'], true)
				.setIn(['email', 'sendAll', 'success'], false)
				.setIn(['email', 'sendAll', 'failure'], '');
		}
		case actionTypes.EMAIL_SEND_ALL_SUCCESS: {
			const { activity, limits } = action.payload;

			return state
				.setIn(['email', 'scheduled', 'activity'], fromJS(activity))
				.setIn(['email', 'scheduled', 'limits'], fromJS(limits))
				.setIn(['email', 'sendAll', 'loading'], false)
				.setIn(['email', 'sendAll', 'success'], true);
		}
		case actionTypes.EMAIL_SEND_ALL_FAILURE: {
			return state
				.setIn(['email', 'sendAll', 'loading'], false)
				.setIn(['email', 'sendAll', 'failure'], action.payload);
		}
		case actionTypes.EMAIL_SEND_ALL_CLEAR: {
			return state
				.setIn(['email', 'sendAll', 'success'], false)
				.setIn(['email', 'sendAll', 'failure'], '');
		}
		case actionTypes.EMAIL_REVERT_AI_TEMPLATE: {
			return state.setIn(['ai', 'revertTemplate'], true);
		}
		case actionTypes.EMAIL_CLEAR_REVERT_AI_TEMPLATE: {
			return state.setIn(['ai', 'revertTemplate'], false);
		}
		case actionTypes.EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA: {
			return state
				.setIn(['reportBadData', 'loading'], true)
				.setIn(['reportBadData', 'success'], false)
				.setIn(['reportBadData', 'failure'], false);
		}
		case actionTypes.EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_SUCCESS: {
			return state
				.setIn(['reportBadData', 'success'], true)
				.setIn(['reportBadData', 'loading'], false)
				.setIn(['reportBadData', 'failure'], false);
		}
		case actionTypes.EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_FAILURE: {
			return state
				.setIn(['reportBadData', 'failure'], true)
				.setIn(['reportBadData', 'loading'], false)
				.setIn(['reportBadData', 'success'], false);
		}
		case actionTypes.EMAIL_SEND_CUSTOMER_REPORT_BAD_DATA_CLEAR: {
			return state
				.setIn(['reportBadData', 'failure'], false)
				.setIn(['reportBadData', 'success'], false)
				.setIn(['reportBadData', 'loading'], false);
		}
		case actionTypes.EMAIL_GET_MONTHLY_EMAIL_LIMIT_SUCCESS: {
			return state.setIn(['email', 'monthlyLimit'], action.payload);
		}
		case actionTypes.EMAIL_GET_EMAIL_TEMPLATES_FAILURE:
		case actionTypes.EMAIL_CREATE_EMAIL_TEMPLATE_FAILURE:
		case actionTypes.EMAIL_DELETE_EMAIL_TEMPLATE_FAILURE:
		default:
			return state;
	}
};
